import React, { useMemo } from "react";
import WordButton from "../WordButton";
import * as styles from "./GameGrid.module.css";
import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";
import { GameStatusContext } from "../../providers/GameStatusProvider";

function WordRow({ words, isAnimating, isProposalPhase, getProposalWord }) {
  return (
    <div className={`grid grid-cols-4 gap-2`}>
      {words.map((word, index) => {
        const proposalWord = isProposalPhase ? getProposalWord(index) : null;
        return (
          <WordButton 
            key={`${word}-${index}`} 
            word={word} 
            fullCandidateSize={words.length}
            isAnimating={isAnimating}
            animationDelay={index * 600 + 500}
            proposalWord={proposalWord}
          />
        );
      })}
    </div>
  );
}

export function SolvedWordRow({ ...props }) {
  const DIFFICULTY_COLOR_MAP = {
    1: "rgb(249 222 109)", // yellow
    2: "rgb(160 196 90)", // green
    3: "rgb(178 197 240)", // blue
    4: "rgb(186 129 197)", // purple
  };

  const color = `${DIFFICULTY_COLOR_MAP[props.difficulty]}`;
  const [hasBeenClicked, setHasBeenClicked] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    // Trigger the animation on mount
    setIsMounted(true);
  }, []);

  const useDisplayWords = props.displayWords != null;

  return (
    <div 
      className={`transition-all duration-success ease-in-out transform ${
        isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
        <div style={{ backgroundColor: color, borderRadius: 8, textAlign: "center" }}>
          <p className="font-bold pt-2 pl-4">{props.category}</p>
          <p className="font-thin pb-2 pl-4">
            {useDisplayWords ? props.displayWords.join(" ")
                : props.words.join(", ")} </p>
        </div>
    </div>
  );
}

function GameGrid({ gameRows, shouldGridShake, setShouldGridShake }) {
  const { 
    submittedGuesses, 
    isGameOver, 
    isGameWon, 
    solvedGameData, 
    isProposalPhase,
    isAnimating,
    getProposalWord
  } = React.useContext(GameStatusContext);

  const { gameData } = React.useContext(PuzzleDataContext);

  React.useEffect(() => {
    const shakeEffect = window.setTimeout(() => {
      setShouldGridShake(false);
    }, 2000);

    return () => window.clearTimeout(shakeEffect);
  }, [submittedGuesses, setShouldGridShake]);

  const isGameOverAndLost = isGameOver && !isGameWon;
  const isGameOverAndWon = isGameOver && isGameWon;
  const isGameActive = !isGameOver;
  const isGameActiveWithAnySolvedRows =
    isGameActive && solvedGameData.length > 0;

  // Process the gameRows prop into display rows
  const wordRows = useMemo(() => {
    if (!gameRows || gameRows.length === 0) return [];
    // If gameRows is already an array of arrays (rows), return it directly
    if (Array.isArray(gameRows[0])) {
      return gameRows;
    }
    // Otherwise, chunk it into rows of 4
    const rows = [];
    for (let i = 0; i < gameRows.length; i += 4) {
      rows.push(gameRows.slice(i, i + 4));
    }
    return rows;
  }, [gameRows]);

  return (
    <div>
      {(isGameOverAndWon || isGameActiveWithAnySolvedRows) && (
        <div className="grid gap-y-2 pb-2">
          {solvedGameData.map((solvedRowObj) => (
            <SolvedWordRow key={solvedRowObj.category} {...solvedRowObj} />
          ))}
        </div>
      )}
      {isGameActive && (
        <div className={`grid gap-y-2 ${shouldGridShake ? styles.shake : ''}`}>
          {wordRows.map((row, idx) => (
            <WordRow 
              key={idx} 
              words={row} 
              isAnimating={isAnimating}
              isProposalPhase={isProposalPhase}
              getProposalWord={getProposalWord}
            />
          ))}
        </div>
      )}
      {isGameOverAndLost && (
        <div className="grid gap-y-2 pb-2">
          <p>The answer categories are below.</p>
          {gameData.map((obj) => (
            <SolvedWordRow key={obj.category} {...obj} />
          ))}
        </div>
      )}
    </div>
  );
}

export default GameGrid;
